import React, { Component, Fragment, ReactNode } from 'react';
import { compose } from 'recompose';
import { clone } from 'lodash';

import { PostAction, PostState, UserGroup } from 'types/app';
import { Error } from 'components';
import { hasCreator } from 'features/content/helpers';
import { USER_FEED_ID } from 'constants/index';
import { isGroupOwnerOrModerator } from 'features/groups/helpers';
import { isAdminUser } from 'helpers';
import {
  IWithErrorHandler,
  IWithFollowed,
  IWithNotify,
  IWithUser,
  withErrorHandler,
  withFollowed,
  withNotify,
  withUser,
} from 'providers';

import { HandleGroupToggle, IPostForm, IPostRenderProps } from '../types';
import { CreatorEditor, ManualEditor, ModeratorEditor } from '../components';

import CreatePostForm from './CreatePostForm';
import EditPostForm from './EditPostForm';

interface IProps {
  action: PostAction;
  children: (props: IPostRenderProps) => ReactNode;
  groupIds?: string[];
  selectedGroup?: UserGroup;
}

interface IPropsEnhanced extends IProps, IWithFollowed, IWithNotify, IWithErrorHandler, IWithUser {}

interface IState {
  postState: PostState;
  isChanging: boolean;
  isWorking: boolean;
}

const enhancers = compose<IPropsEnhanced, IProps>(
  withErrorHandler,
  withFollowed('postable'),
  withNotify,
  withUser,
);

class Post extends Component<IPropsEnhanced> {
  state: IState = {
    postState: PostState.View,
    isChanging: false,
    isWorking: false,
  };

  getOrderedGroups() {
    const { selectedGroup, postable } = this.props;
    const { list } = postable;

    if (!selectedGroup) {
      return list;
    }

    const clonedList = clone(list);
    const index = clonedList.findIndex(item => item.id === selectedGroup.id);
    const removed = clonedList.splice(index, 1);

    return removed.concat(clonedList);
  }

  getGroupsWithoutTimeline() {
    const { postable } = this.props;
    const { list } = postable;

    return list.filter((group: UserGroup) => group.id !== USER_FEED_ID);
  }

  toggleState = (postState: PostState) => {
    const { isChanging } = this.state;
    this.setState({ isChanging: !isChanging, postState });
  };

  toggleWork = () => {
    const { isWorking } = this.state;
    this.setState({ isWorking: !isWorking });
  };

  handleGroupToggle: HandleGroupToggle = values => (group, arrayHelpers) => {
    const { group_ids } = values;
    const currentIndex = group_ids.findIndex(id => id === group.id);

    currentIndex >= 0 ? arrayHelpers.remove(currentIndex) : arrayHelpers.push(group.id);
  };

  handleError: IPostForm['handleError'] = error => {
    const { onError } = this.props;

    this.toggleWork();
    onError(error);
  };

  handleCompleted: IPostForm['handleCompleted'] = (i18nKey: string, showNotification: boolean) => {
    const { notify } = this.props;

    this.toggleState(PostState.View);
    this.toggleWork();

    if (showNotification) {
      notify.info({ i18n: { i18nKey } });
    }
  };

  renderError() {
    const { error, onErrorClose } = this.props;
    return <Error key={error} message={error} onClose={onErrorClose} />;
  }

  renderEditor: IPostForm['renderEditor'] = props => {
    const { action, user, selectedGroup } = this.props;
    const { isWorking, postState } = this.state;
    const { values } = props;

    const groupsExcludingTimeline = this.getGroupsWithoutTimeline();
    const orderedBySelectedGroups = this.getOrderedGroups();

    const isEditing = postState === PostState.Edit;
    const isCustomizing = postState === PostState.Customize;
    const isUserPost = hasCreator(values);
    const adminCanEdit = isCustomizing && isAdminUser(user) && !isUserPost;
    const moderatorCanEdit = isGroupOwnerOrModerator(user, selectedGroup && selectedGroup.id);
    const groups = isEditing ? orderedBySelectedGroups : groupsExcludingTimeline;

    if (isEditing || adminCanEdit) {
      return (
        <CreatorEditor
          groups={groups}
          action={action}
          working={isWorking}
          canModerate={moderatorCanEdit}
          onClose={() => this.toggleState(PostState.View)}
          onGroupToggle={this.handleGroupToggle(values)}
        />
      );
    }

    if (isCustomizing && moderatorCanEdit) {
      return (
        <ModeratorEditor
          groups={groups}
          working={isWorking}
          onClose={() => this.toggleState(PostState.View)}
          onGroupToggle={this.handleGroupToggle(values)}
        />
      );
    }

    return (
      <ManualEditor
        groups={groups}
        working={isWorking}
        onClose={() => this.toggleState(PostState.View)}
        onGroupToggle={this.handleGroupToggle(values)}
      />
    );
  };

  render() {
    const { action, children, selectedGroup, groupIds } = this.props;
    const { isChanging } = this.state;

    const ModeComponent = action === PostAction.Create ? CreatePostForm : EditPostForm;
    const props: IPostForm = {
      handleError: this.handleError,
      handleCompleted: this.handleCompleted,
      renderEditor: this.renderEditor,
    };

    const stateProps: IPostRenderProps = {
      isChanging,
      selectedGroup,
      groupIds,
      toggleState: this.toggleState,
      toggleWork: this.toggleWork,
    };

    return (
      <Fragment>
        {this.renderError()}
        <ModeComponent {...props} {...stateProps}>
          {children(stateProps)}
        </ModeComponent>
      </Fragment>
    );
  }
}

export default enhancers(Post);
